import React, { useState, useEffect, useRef, useCallback } from "react";
import { APIProvider, Map, AdvancedMarker } from "@vis.gl/react-google-maps";
import { FaCircle } from "react-icons/fa";
import YouTube from "react-youtube";

import "./styles.scss";

const MAPS_API_KEY = "AIzaSyBvb0_gNSOXPteCUORaMgKh6cWXJHBysR8";
const MAP_ID = "a2b3caaf911f43d3f7c05ccb";
const CHURCH_LOCATION = { lat: 9.967688163987908, lng: 76.42079918312011 };
const HALL_LOCATION = { lat: 9.9794626642496, lng: 76.51460209732615 };
/* ---------- Calendar link generation ---------- */
const CALENDAR_LINKS = (() => {
  const enc = encodeURIComponent;
  const title = enc("Divins + Liya | Wedding");
  const loc = enc("Little Flower Syro-Malabar Church, Puthencruz");
  const desc = enc(
    "Divins weds Liya. " +
      "Come join us for our wedding ceremony at Little Flower Church, Puthencruz, " +
      "followed by reception at Eden Convention Centre, Muvattupuzha on 2nd May 2026. " +
      "More details: https://divins.dev/wedding",
  );
  // IST (UTC+5:30)  15:15–22:00  →  UTC  09:45–16:30
  const sg = "20260502T094500Z";
  const eg = "20260502T163000Z";
  return {
    google: `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${sg}%2F${eg}&details=${desc}&location=${loc}`,
    ics: "/wedding/invitation.ics",
  };
})();

const CAL_OPTIONS = [
  { label: "Google Calendar", href: CALENDAR_LINKS.google, external: true },
  {
    label: "Apple Calendar",
    href: CALENDAR_LINKS.ics,
    download: "invitation.ics",
  },
];

const AddToCalendarButton = ({ triggerClassName, label }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onMouse = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onMouse);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onMouse);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  return (
    <div className="d3__atc-wrap" ref={wrapRef}>
      <button
        className={triggerClassName}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        {label}
      </button>
      {open && (
        <div className="d3__atc-dropdown" role="menu">
          {CAL_OPTIONS.map(({ label: optLabel, href, external, download }) => (
            <a
              key={optLabel}
              href={href}
              role="menuitem"
              target={external ? "_blank" : undefined}
              rel="noreferrer"
              download={download}
              onClick={() => setOpen(false)}
            >
              {optLabel}
            </a>
          ))}
        </div>
      )}
    </div>
  );
};
/* ----------------------------------------------- */

const STORY_VIDEO = {
  id: "hsx2mPSxPkU",
  title: "Our Story Video",
  aspectRatio: "16 / 9",
  aspectRatioValue: 16 / 9,
};

const VenuePin = () => (
  <div className="d3__map-pin">
    <div className="d3__map-pin-body">
      <FaCircle size={20} color="#faf6f4" />
    </div>
  </div>
);

const WEDDING_DATE = new Date("2026-05-02T17:00:00");

const getTimeRemaining = () => {
  const diff = WEDDING_DATE - new Date();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff / 3600000) % 24),
    minutes: Math.floor((diff / 60000) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
};

const pad = (n) => String(n).padStart(2, "0");

const SCHEDULE = [
  {
    time: "3:15 PM",
    event: "Ceremony at the church",
    desc: "Little Flower Church, Puthencruz",
  },
  {
    time: "4:00 PM",
    event: "Say yes, I do!",
    desc: "",
  },
  {
    time: "5:00 PM",
    event: "Bride's ceremonial home entry",
    desc: "Groom's Residence, Puthencruz",
  },
  {
    time: "6:30 PM",
    event: "Welcoming the newly weds",
    desc: "Eden Convention Centre, Muvattupuzha",
  },
  {
    time: "7:00 PM",
    event: "Dinner time",
    desc: "",
  },
  {
    time: "8:30 PM",
    event: "Dance the night away",
    desc: "",
  },
];

const ROMAN = ["I", "II", "III", "IV", "V", "VI"];
const CONFETTI_COLORS = ["#fffaf2", "#f4eadc", "#e6d7c5", "#d7c3aa", "#ffffff"];
const CONFETTI_PIECES = Array.from({ length: 56 }, (_, i) => ({
  id: i,
  x: `${(i * 17) % 100}%`,
  drift: `${((i % 9) - 4) * 14}px`,
  delay: `${(i % 14) * 0.12}s`,
  duration: `${5.6 + (i % 6) * 0.35}s`,
  width: `${5 + (i % 4) * 2}px`,
  height: `${12 + (i % 5) * 4}px`,
  spin: `${180 + (i % 7) * 90}deg`,
  color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
  shape: i % 3 === 0 ? "d3__confetti-piece--petal" : "",
}));

const Design3 = () => {
  const [countdown, setCountdown] = useState(getTimeRemaining);
  const [navVisible, setNavVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [lightboxItem, setLightboxItem] = useState(null);
  const [lightboxClosing, setLightboxClosing] = useState(false);

  const closeLightbox = useCallback(() => {
    setLightboxClosing(true);
  }, []);

  const openImageLightbox = useCallback((src) => {
    setLightboxItem({ type: "image", src });
  }, []);

  const openVideoLightbox = useCallback(
    (videoId, title, aspectRatio, aspectRatioValue) => {
      setLightboxItem({
        type: "youtube",
        videoId,
        title,
        aspectRatio,
        aspectRatioValue,
      });
    },
    [],
  );

  const homeRef = useRef(null);
  const familyRef = useRef(null);
  const scheduleRef = useRef(null);
  const venueRef = useRef(null);
  const countdownRef = useRef(null);
  const galleryRef = useRef(null);

  useEffect(() => {
    const prevTitle = document.title;
    const descEl = document.querySelector('meta[name="description"]');
    const prevDesc = descEl?.getAttribute("content") ?? "";

    document.title = "Divins + Liya · Wedding · May 2, 2026";
    descEl?.setAttribute(
      "content",
      "Join us as we celebrate the wedding of Divins and Liya on May 2, 2026, at Little Flower Church, Puthencruz, Kochi, Kerala.",
    );

    return () => {
      document.title = prevTitle;
      descEl?.setAttribute("content", prevDesc);
    };
  }, []);

  useEffect(() => {
    const link = document.querySelector("link[rel='icon']");
    const prevHref = link?.getAttribute("href") ?? "/favicon.png";
    if (link) link.setAttribute("href", "/wedding/images/logo.png");
    return () => {
      if (link) link.setAttribute("href", prevHref);
    };
  }, []);

  useEffect(() => {
    const t = setInterval(() => setCountdown(getTimeRemaining()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(h > 0 ? (y / h) * 100 : 0);
      setNavVisible(y > 60);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach(
          (e) => e.isIntersecting && e.target.classList.add("visible"),
        ),
      { threshold: 0.1 },
    );
    document
      .querySelectorAll(".d3-animate")
      .forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const scrollTo = useCallback((ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const countdownComplete =
    countdown.days === 0 &&
    countdown.hours === 0 &&
    countdown.minutes === 0 &&
    countdown.seconds === 0;

  return (
    <div className="d3">
      <div className="d3__progress" style={{ width: `${progress}%` }} />
      <nav className={`d3__nav${navVisible ? " visible" : ""}`}>
        <div className="d3__nav-logo">Divins &amp; Liya</div>
        <div className="d3__nav-links">
          {[
            ["Home", homeRef],
            ["Family", familyRef],
            ["Schedule", scheduleRef],
            ["Venue", venueRef],
            ["Gallery", galleryRef],
            ["Countdown", countdownRef],
          ].map(([label, ref]) => (
            <button key={label} onClick={() => scrollTo(ref)}>
              {label}
            </button>
          ))}
        </div>
        <button
          className={`d3__nav-hamburger${menuOpen ? " open" : ""}`}
          onClick={() => setMenuOpen((v) => !v)}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
        >
          <span />
          <span />
          <span />
        </button>
      </nav>
      {/* ── MOBILE DRAWER ── */}
      <div
        className={`d3__nav-drawer${menuOpen ? " open" : ""}`}
        aria-hidden={!menuOpen}
        role="dialog"
      >
        <div className="d3__nav-drawer-inner">
          <p className="d3__nav-drawer-logo">Divins &amp; Liya</p>
          <div className="d3__nav-drawer-ornament">
            <span />
            <span className="d3__nav-drawer-diamond" />
            <span />
          </div>
          <div className="d3__nav-drawer-links">
            {[
              ["Home", homeRef],
              ["Family", familyRef],
              ["Schedule", scheduleRef],
              ["Venue", venueRef],
              ["Gallery", galleryRef],
              ["Countdown", countdownRef],
            ].map(([label, ref], i) => (
              <button
                key={label}
                style={{ "--i": i }}
                onClick={() => {
                  scrollTo(ref);
                  setMenuOpen(false);
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="d3__nav-drawer-date">May 2 · 2026</p>
        </div>
      </div>
      {/* ── HOME ── */}
      <section ref={homeRef} className="d3__hero">
        <div className="d3__hero-bg">
          <img
            className="d3__hero-bg-img"
            src="/wedding/images/ummmmma.webp"
            alt=""
          />
        </div>
        <div className="d3__hero-vignette" />
        <div className="d3__hero-ornament-top" aria-hidden="true">
          <div className="d3__hero-ornament-line" />
          <div className="d3__hero-ornament-dot" />
          <div className="d3__hero-ornament-line" />
        </div>
        <div className="d3__hero-content">
          <p className="d3__hero-sup">Together Forever</p>
          <h1 className="d3__hero-names" aria-label="Divins and Liya">
            <svg
              className="d3__handwritten"
              xmlns="http://www.w3.org/2000/svg"
              xmlnsXlink="http://www.w3.org/1999/xlink"
              viewBox="0 0 358.5 311.25"
              preserveAspectRatio="xMidYMid meet"
              aria-hidden="true"
            >
              <defs>
                <clipPath id="d3hw-c1">
                  <rect x="0" width="178" y="0" height="125" />
                </clipPath>
                <clipPath id="d3hw-c2">
                  <rect x="0" width="32" y="0" height="70" />
                </clipPath>
                <clipPath id="d3hw-c3">
                  <path
                    clipRule="nonzero"
                    d="M 256 164 L 352 164 L 352 191 L 256 191 Z M 256 164 "
                  />
                </clipPath>
                <clipPath id="d3hw-c4">
                  <path
                    clipRule="nonzero"
                    d="M 256.144531 185.597656 L 349.914062 159.753906 L 352.699219 169.851562 L 258.929688 195.695312 Z M 256.144531 185.597656 "
                  />
                </clipPath>
                <clipPath id="d3hw-c5">
                  <path
                    clipRule="nonzero"
                    d="M 256.144531 185.597656 L 349.914062 159.753906 L 352.699219 169.851562 L 258.929688 195.695312 Z M 256.144531 185.597656 "
                  />
                </clipPath>
                <clipPath id="d3hw-c6">
                  <path
                    clipRule="nonzero"
                    d="M 18 117 L 106 117 L 106 162 L 18 162 Z M 18 117 "
                  />
                </clipPath>
                <clipPath id="d3hw-c7">
                  <path
                    clipRule="nonzero"
                    d="M 18.390625 152.199219 L 102.136719 117.8125 L 105.832031 126.808594 L 22.085938 161.199219 Z M 18.390625 152.199219 "
                  />
                </clipPath>
                <clipPath id="d3hw-c8">
                  <path
                    clipRule="nonzero"
                    d="M 4 1 L 88 1 L 88 44.371094 L 4 44.371094 Z M 4 1 "
                  />
                </clipPath>
                <clipPath id="d3hw-c9">
                  <path
                    clipRule="nonzero"
                    d="M 0.390625 35.199219 L 84.136719 0.8125 L 87.832031 9.808594 L 4.085938 44.199219 Z M 0.390625 35.199219 "
                  />
                </clipPath>
                <clipPath id="d3hw-c10">
                  <path
                    clipRule="nonzero"
                    d="M 4.1875 44.445312 L 94.164062 7.5 L 90.183594 -2.1875 L 0.207031 34.757812 Z M 4.1875 44.445312 "
                  />
                </clipPath>
                <clipPath id="d3hw-c11">
                  <rect x="0" width="88" y="0" height="45" />
                </clipPath>
                <clipPath id="d3hw-c12">
                  <rect x="0" width="130" y="0" height="137" />
                </clipPath>
              </defs>

              {/* ── DIVINS ── */}
              <g transform="matrix(1, 0, 0, 1, 90, 54)">
                <g clipPath="url(#d3hw-c1)">
                  {/* D */}
                  <g className="d3-letter" style={{ "--d": "0.5s" }}>
                    <g transform="translate(10.745057, 88.644163)">
                      <g>
                        <path
                          pathLength="1"
                          d="M 37.46875 -65.75 C 38.570312 -65.3125 39.707031 -64.632812 40.875 -63.71875 C 42.039062 -62.800781 43.117188 -61.566406 44.109375 -60.015625 C 45.109375 -58.472656 45.910156 -56.515625 46.515625 -54.140625 C 47.117188 -51.773438 47.460938 -48.925781 47.546875 -45.59375 C 47.648438 -37.738281 46.722656 -29.90625 44.765625 -22.09375 C 42.816406 -14.289062 39.953125 -6.984375 36.171875 -0.171875 C 34.003906 3.660156 31.710938 7.0625 29.296875 10.03125 C 26.878906 13.007812 24.46875 15.394531 22.0625 17.1875 C 19.664062 18.988281 17.4375 20.054688 15.375 20.390625 C 15.332031 20.390625 15.289062 20.394531 15.25 20.40625 C 14.726562 20.488281 14.21875 20.503906 13.71875 20.453125 C 13.695312 22.117188 13.695312 23.628906 13.71875 24.984375 C 13.75 26.347656 13.804688 27.519531 13.890625 28.5 C 13.929688 28.28125 13.976562 28.066406 14.03125 27.859375 C 14.101562 27.503906 14.28125 27.242188 14.5625 27.078125 C 14.84375 26.921875 15.132812 26.878906 15.4375 26.953125 C 15.789062 27.003906 16.046875 27.171875 16.203125 27.453125 C 16.367188 27.742188 16.421875 28.046875 16.359375 28.359375 C 15.960938 30.035156 15.582031 31.296875 15.21875 32.140625 C 14.863281 32.984375 14.40625 33.445312 13.84375 33.53125 C 13.71875 33.550781 13.570312 33.535156 13.40625 33.484375 C 13.101562 33.453125 12.820312 33.296875 12.5625 33.015625 C 12.3125 32.742188 12.097656 32.175781 11.921875 31.3125 C 11.753906 30.445312 11.613281 29.101562 11.5 27.28125 C 11.394531 25.46875 11.359375 23.007812 11.390625 19.90625 C 9.554688 19.03125 8.296875 17.328125 7.609375 14.796875 C 7.515625 14.484375 7.546875 14.175781 7.703125 13.875 C 7.859375 13.582031 8.09375 13.390625 8.40625 13.296875 C 8.726562 13.203125 9.023438 13.238281 9.296875 13.40625 C 9.578125 13.570312 9.765625 13.8125 9.859375 14.125 C 10.222656 15.4375 10.722656 16.410156 11.359375 17.046875 C 11.410156 13.734375 11.5 9.703125 11.625 4.953125 C 11.75 0.210938 11.929688 -5.390625 12.171875 -11.859375 C 12.421875 -18.335938 12.726562 -25.847656 13.09375 -34.390625 C 13.238281 -38.585938 13.382812 -42.5625 13.53125 -46.3125 C 13.675781 -50.0625 13.816406 -53.441406 13.953125 -56.453125 C 13.367188 -55.941406 12.832031 -55.4375 12.34375 -54.9375 C 10.300781 -52.914062 8.425781 -50.597656 6.71875 -47.984375 C 5.007812 -45.367188 3.632812 -42.519531 2.59375 -39.4375 C 1.5625 -36.351562 1.035156 -33.132812 1.015625 -29.78125 C 0.992188 -26.425781 1.675781 -23.015625 3.0625 -19.546875 C 3.15625 -19.234375 3.140625 -18.929688 3.015625 -18.640625 C 2.898438 -18.359375 2.6875 -18.148438 2.375 -18.015625 C 2.0625 -17.921875 1.757812 -17.9375 1.46875 -18.0625 C 1.1875 -18.1875 0.976562 -18.398438 0.84375 -18.703125 C -0.625 -22.441406 -1.351562 -26.109375 -1.34375 -29.703125 C -1.34375 -33.304688 -0.789062 -36.75 0.3125 -40.03125 C 1.414062 -43.3125 2.875 -46.359375 4.6875 -49.171875 C 6.507812 -51.992188 8.492188 -54.46875 10.640625 -56.59375 C 11.753906 -57.675781 12.875 -58.6875 14 -59.625 C 14.050781 -61.375 14.101562 -62.863281 14.15625 -64.09375 C 14.144531 -64.4375 14.253906 -64.71875 14.484375 -64.9375 C 14.722656 -65.164062 15.003906 -65.265625 15.328125 -65.234375 C 15.660156 -65.242188 15.9375 -65.128906 16.15625 -64.890625 C 16.382812 -64.660156 16.503906 -64.382812 16.515625 -64.0625 C 16.472656 -63.3125 16.453125 -62.414062 16.453125 -61.375 C 20.097656 -63.914062 23.78125 -65.585938 27.5 -66.390625 C 31.21875 -67.191406 34.539062 -66.976562 37.46875 -65.75 Z M 45.203125 -45.515625 C 45.085938 -50.335938 44.304688 -54.273438 42.859375 -57.328125 C 41.410156 -60.390625 39.316406 -62.488281 36.578125 -63.625 C 33.847656 -64.71875 30.671875 -64.796875 27.046875 -63.859375 C 23.429688 -62.929688 19.867188 -61.125 16.359375 -58.4375 C 16.242188 -55.28125 16.101562 -51.640625 15.9375 -47.515625 C 15.78125 -43.390625 15.601562 -38.972656 15.40625 -34.265625 C 15.238281 -30.179688 15.0625 -25.8125 14.875 -21.15625 C 14.6875 -16.507812 14.515625 -11.835938 14.359375 -7.140625 C 14.210938 -2.453125 14.070312 2.050781 13.9375 6.375 C 13.8125 10.707031 13.738281 14.617188 13.71875 18.109375 C 14.09375 18.128906 14.484375 18.128906 14.890625 18.109375 C 14.929688 18.097656 14.972656 18.09375 15.015625 18.09375 C 16.503906 17.851562 18.296875 16.976562 20.390625 15.46875 C 22.492188 13.96875 24.726562 11.828125 27.09375 9.046875 C 29.457031 6.265625 31.78125 2.8125 34.0625 -1.3125 C 35.164062 -3.226562 36.359375 -5.644531 37.640625 -8.5625 C 38.929688 -11.488281 40.160156 -14.835938 41.328125 -18.609375 C 42.503906 -22.378906 43.460938 -26.523438 44.203125 -31.046875 C 44.953125 -35.566406 45.285156 -40.390625 45.203125 -45.515625 Z M 45.203125 -45.515625 "
                        />
                      </g>
                    </g>
                  </g>
                  {/* i */}
                  <g className="d3-letter" style={{ "--d": "0.72s" }}>
                    <g transform="translate(62.234341, 80.364057)">
                      <g>
                        <path
                          pathLength="1"
                          d="M 15.5 -16.640625 C 15.8125 -16.535156 16.023438 -16.328125 16.140625 -16.015625 C 16.253906 -15.703125 16.253906 -15.390625 16.140625 -15.078125 C 15.554688 -13.734375 14.800781 -12.222656 13.875 -10.546875 C 12.957031 -8.867188 11.914062 -7.242188 10.75 -5.671875 C 9.59375 -4.109375 8.410156 -2.769531 7.203125 -1.65625 C 6.003906 -0.550781 4.835938 0.0859375 3.703125 0.265625 C 3.222656 0.347656 2.785156 0.335938 2.390625 0.234375 C 0.929688 -0.160156 -0.0546875 -1.503906 -0.578125 -3.796875 C -1.097656 -6.085938 -1.148438 -9.441406 -0.734375 -13.859375 C -0.703125 -14.179688 -0.554688 -14.445312 -0.296875 -14.65625 C -0.046875 -14.863281 0.222656 -14.953125 0.515625 -14.921875 C 0.847656 -14.890625 1.117188 -14.742188 1.328125 -14.484375 C 1.535156 -14.234375 1.625 -13.957031 1.59375 -13.65625 C 1.363281 -10.96875 1.285156 -8.851562 1.359375 -7.3125 C 1.441406 -5.78125 1.597656 -4.640625 1.828125 -3.890625 C 2.054688 -3.140625 2.289062 -2.648438 2.53125 -2.421875 C 2.769531 -2.191406 2.910156 -2.082031 2.953125 -2.09375 C 3.523438 -1.925781 4.265625 -2.21875 5.171875 -2.96875 C 6.085938 -3.71875 7.078125 -4.769531 8.140625 -6.125 C 9.203125 -7.476562 10.242188 -9.007812 11.265625 -10.71875 C 12.296875 -12.4375 13.191406 -14.207031 13.953125 -16.03125 C 14.097656 -16.34375 14.316406 -16.550781 14.609375 -16.65625 C 14.910156 -16.757812 15.207031 -16.753906 15.5 -16.640625 Z M 0.453125 -19.203125 C -0.359375 -19.109375 -0.8125 -19.269531 -0.90625 -19.6875 C -1 -20.113281 -0.882812 -20.578125 -0.5625 -21.078125 C -0.25 -21.578125 0.09375 -21.941406 0.46875 -22.171875 C 1.082031 -22.472656 1.5625 -22.507812 1.90625 -22.28125 C 2.257812 -22.050781 2.460938 -21.710938 2.515625 -21.265625 C 2.566406 -20.816406 2.425781 -20.378906 2.09375 -19.953125 C 1.769531 -19.523438 1.222656 -19.273438 0.453125 -19.203125 Z M 0.453125 -19.203125 "
                        />
                      </g>
                    </g>
                  </g>
                  {/* v */}
                  <g className="d3-letter" style={{ "--d": "0.94s" }}>
                    <g transform="translate(77.541971, 77.902403)">
                      <g>
                        <path
                          pathLength="1"
                          d="M 28.25 -18.078125 C 28.570312 -17.921875 28.769531 -17.679688 28.84375 -17.359375 C 28.914062 -17.046875 28.867188 -16.75 28.703125 -16.46875 C 27.453125 -14.457031 25.90625 -12.898438 24.0625 -11.796875 C 22.21875 -10.691406 20.363281 -10.234375 18.5 -10.421875 C 16.351562 -10.617188 14.554688 -11.632812 13.109375 -13.46875 C 12.660156 -9.800781 11.6875 -6.101562 10.1875 -2.375 C 9.175781 0.144531 8.085938 2.273438 6.921875 4.015625 C 5.753906 5.753906 4.628906 6.894531 3.546875 7.4375 C 3.171875 7.625 2.800781 7.742188 2.4375 7.796875 C 1.988281 7.867188 1.597656 7.835938 1.265625 7.703125 C 0.941406 7.578125 0.691406 7.46875 0.515625 7.375 C 0.316406 7.1875 0.0507812 6.898438 -0.28125 6.515625 C -0.613281 6.140625 -0.90625 5.484375 -1.15625 4.546875 C -1.40625 3.617188 -1.570312 2.25 -1.65625 0.4375 C -1.75 -1.363281 -1.671875 -3.738281 -1.421875 -6.6875 C -1.171875 -9.644531 -0.675781 -13.335938 0.0625 -17.765625 C 0.101562 -18.046875 0.257812 -18.285156 0.53125 -18.484375 C 0.8125 -18.679688 1.097656 -18.757812 1.390625 -18.71875 C 1.734375 -18.644531 2 -18.476562 2.1875 -18.21875 C 2.375 -17.96875 2.4375 -17.671875 2.375 -17.328125 C 1.726562 -13.328125 1.28125 -9.972656 1.03125 -7.265625 C 0.789062 -4.566406 0.675781 -2.378906 0.6875 -0.703125 C 0.707031 0.972656 0.796875 2.238281 0.953125 3.09375 C 1.117188 3.957031 1.300781 4.550781 1.5 4.875 C 1.695312 5.195312 1.816406 5.375 1.859375 5.40625 C 1.910156 5.4375 1.96875 5.457031 2.03125 5.46875 C 2.09375 5.488281 2.238281 5.4375 2.46875 5.3125 C 3.269531 4.894531 4.164062 3.910156 5.15625 2.359375 C 6.15625 0.816406 7.101562 -1.070312 8 -3.3125 C 8.90625 -5.550781 9.632812 -7.910156 10.1875 -10.390625 C 10.738281 -12.878906 10.992188 -15.273438 10.953125 -17.578125 C 10.929688 -18.253906 11.257812 -18.660156 11.9375 -18.796875 C 12.582031 -18.898438 13.019531 -18.640625 13.25 -18.015625 C 14.4375 -14.734375 16.265625 -12.984375 18.734375 -12.765625 C 20.140625 -12.617188 21.566406 -12.988281 23.015625 -13.875 C 24.460938 -14.769531 25.671875 -16.039062 26.640625 -17.6875 C 26.847656 -17.976562 27.097656 -18.15625 27.390625 -18.21875 C 27.691406 -18.289062 27.976562 -18.242188 28.25 -18.078125 Z M 28.25 -18.078125 "
                        />
                      </g>
                    </g>
                  </g>
                  {/* i */}
                  <g className="d3-letter" style={{ "--d": "1.16s" }}>
                    <g transform="translate(106.100067, 73.309913)">
                      <g>
                        <path
                          pathLength="1"
                          d="M 15.5 -16.640625 C 15.8125 -16.535156 16.023438 -16.328125 16.140625 -16.015625 C 16.253906 -15.703125 16.253906 -15.390625 16.140625 -15.078125 C 15.554688 -13.734375 14.800781 -12.222656 13.875 -10.546875 C 12.957031 -8.867188 11.914062 -7.242188 10.75 -5.671875 C 9.59375 -4.109375 8.410156 -2.769531 7.203125 -1.65625 C 6.003906 -0.550781 4.835938 0.0859375 3.703125 0.265625 C 3.222656 0.347656 2.785156 0.335938 2.390625 0.234375 C 0.929688 -0.160156 -0.0546875 -1.503906 -0.578125 -3.796875 C -1.097656 -6.085938 -1.148438 -9.441406 -0.734375 -13.859375 C -0.703125 -14.179688 -0.554688 -14.445312 -0.296875 -14.65625 C -0.046875 -14.863281 0.222656 -14.953125 0.515625 -14.921875 C 0.847656 -14.890625 1.117188 -14.742188 1.328125 -14.484375 C 1.535156 -14.234375 1.625 -13.957031 1.59375 -13.65625 C 1.363281 -10.96875 1.285156 -8.851562 1.359375 -7.3125 C 1.441406 -5.78125 1.597656 -4.640625 1.828125 -3.890625 C 2.054688 -3.140625 2.289062 -2.648438 2.53125 -2.421875 C 2.769531 -2.191406 2.910156 -2.082031 2.953125 -2.09375 C 3.523438 -1.925781 4.265625 -2.21875 5.171875 -2.96875 C 6.085938 -3.71875 7.078125 -4.769531 8.140625 -6.125 C 9.203125 -7.476562 10.242188 -9.007812 11.265625 -10.71875 C 12.296875 -12.4375 13.191406 -14.207031 13.953125 -16.03125 C 14.097656 -16.34375 14.316406 -16.550781 14.609375 -16.65625 C 14.910156 -16.757812 15.207031 -16.753906 15.5 -16.640625 Z M 0.453125 -19.203125 C -0.359375 -19.109375 -0.8125 -19.269531 -0.90625 -19.6875 C -1 -20.113281 -0.882812 -20.578125 -0.5625 -21.078125 C -0.25 -21.578125 0.09375 -21.941406 0.46875 -22.171875 C 1.082031 -22.472656 1.5625 -22.507812 1.90625 -22.28125 C 2.257812 -22.050781 2.460938 -21.710938 2.515625 -21.265625 C 2.566406 -20.816406 2.425781 -20.378906 2.09375 -19.953125 C 1.769531 -19.523438 1.222656 -19.273438 0.453125 -19.203125 Z M 0.453125 -19.203125 "
                        />
                      </g>
                    </g>
                  </g>
                  {/* n */}
                  <g className="d3-letter" style={{ "--d": "1.38s" }}>
                    <g transform="translate(121.407698, 70.848259)">
                      <g>
                        <path
                          pathLength="1"
                          d="M 22.21875 -17.421875 C 22.53125 -17.296875 22.75 -17.085938 22.875 -16.796875 C 23 -16.515625 23.003906 -16.21875 22.890625 -15.90625 C 21.535156 -12.25 20.242188 -9.398438 19.015625 -7.359375 C 17.796875 -5.328125 16.382812 -4.289062 14.78125 -4.25 C 12.726562 -4.15625 10.785156 -6.070312 8.953125 -10 C 8.328125 -11.269531 7.816406 -12.171875 7.421875 -12.703125 C 7.023438 -13.242188 6.722656 -13.570312 6.515625 -13.6875 C 6.316406 -13.800781 6.175781 -13.851562 6.09375 -13.84375 C 5.957031 -13.863281 5.726562 -13.695312 5.40625 -13.34375 C 5.09375 -13 4.671875 -12.289062 4.140625 -11.21875 C 3.617188 -10.144531 3.019531 -8.507812 2.34375 -6.3125 C 1.664062 -4.113281 0.882812 -1.117188 0 2.671875 C -0.125 3.179688 -0.429688 3.476562 -0.921875 3.5625 C -1.046875 3.582031 -1.148438 3.578125 -1.234375 3.546875 C -1.941406 3.503906 -2.316406 3.125 -2.359375 2.40625 C -2.410156 0.726562 -2.347656 -1.238281 -2.171875 -3.5 C -1.992188 -5.757812 -1.773438 -8.066406 -1.515625 -10.421875 C -1.253906 -12.785156 -1.003906 -14.894531 -0.765625 -16.75 C -0.722656 -17.175781 -0.675781 -17.566406 -0.625 -17.921875 C -0.570312 -18.285156 -0.53125 -18.640625 -0.5 -18.984375 C -0.425781 -19.316406 -0.257812 -19.578125 0 -19.765625 C 0.25 -19.953125 0.523438 -20.023438 0.828125 -19.984375 C 1.148438 -19.960938 1.414062 -19.820312 1.625 -19.5625 C 1.84375 -19.3125 1.914062 -19.015625 1.84375 -18.671875 C 1.8125 -18.335938 1.769531 -17.988281 1.71875 -17.625 C 1.675781 -17.269531 1.628906 -16.878906 1.578125 -16.453125 C 1.460938 -15.359375 1.320312 -14.15625 1.15625 -12.84375 C 1 -11.539062 0.835938 -10.210938 0.671875 -8.859375 C 1.640625 -11.660156 2.535156 -13.5625 3.359375 -14.5625 C 4.191406 -15.570312 5.019531 -16.125 5.84375 -16.21875 C 6.976562 -16.3125 7.960938 -15.828125 8.796875 -14.765625 C 9.628906 -13.703125 10.394531 -12.441406 11.09375 -10.984375 C 12.03125 -8.972656 12.804688 -7.726562 13.421875 -7.25 C 14.046875 -6.769531 14.472656 -6.550781 14.703125 -6.59375 C 15.242188 -6.59375 15.8125 -6.976562 16.40625 -7.75 C 17.007812 -8.53125 17.582031 -9.492188 18.125 -10.640625 C 18.664062 -11.785156 19.164062 -12.910156 19.625 -14.015625 C 20.09375 -15.128906 20.4375 -16.03125 20.65625 -16.71875 C 20.769531 -17.039062 20.984375 -17.269531 21.296875 -17.40625 C 21.609375 -17.539062 21.914062 -17.546875 22.21875 -17.421875 Z M 22.21875 -17.421875 "
                        />
                      </g>
                    </g>
                  </g>
                  {/* s */}
                  <g className="d3-letter" style={{ "--d": "1.6s" }}>
                    <g transform="translate(143.007793, 67.3747)">
                      <g>
                        <path
                          pathLength="1"
                          d="M 24.703125 -18.1875 C 24.972656 -18.03125 25.148438 -17.789062 25.234375 -17.46875 C 25.328125 -17.15625 25.28125 -16.851562 25.09375 -16.5625 C 24.132812 -15.007812 23.082031 -13.289062 21.9375 -11.40625 C 20.789062 -9.53125 19.664062 -7.695312 18.5625 -5.90625 C 17.46875 -4.113281 16.523438 -2.5625 15.734375 -1.25 C 14.953125 0.0507812 14.441406 0.925781 14.203125 1.375 C 13.648438 2.375 13.117188 3.347656 12.609375 4.296875 C 13.515625 6.554688 13.921875 8.757812 13.828125 10.90625 C 13.742188 13.050781 13.335938 15.007812 12.609375 16.78125 C 11.878906 18.5625 11.039062 19.984375 10.09375 21.046875 C 9.113281 22.191406 8.140625 22.84375 7.171875 23 C 6.566406 23.09375 6.03125 22.972656 5.5625 22.640625 C 4.769531 22.097656 4.382812 21.171875 4.40625 19.859375 C 4.425781 18.554688 4.707031 17.035156 5.25 15.296875 C 5.800781 13.554688 6.503906 11.726562 7.359375 9.8125 C 8.210938 7.894531 9.09375 6.035156 10 4.234375 C 9.675781 3.535156 9.269531 2.84375 8.78125 2.15625 C 8.300781 1.46875 7.753906 0.773438 7.140625 0.078125 C 5.085938 -2.148438 3.3125 -4.269531 1.8125 -6.28125 C 0.3125 -8.289062 -0.539062 -10.210938 -0.75 -12.046875 C -1.09375 -15.429688 -0.00390625 -18.171875 2.515625 -20.265625 C 3.535156 -21.128906 4.660156 -21.722656 5.890625 -22.046875 C 7.117188 -22.378906 8.300781 -22.390625 9.4375 -22.078125 C 9.726562 -21.953125 9.953125 -21.742188 10.109375 -21.453125 C 10.265625 -21.171875 10.300781 -20.882812 10.21875 -20.59375 C 10.101562 -20.269531 9.90625 -20.03125 9.625 -19.875 C 9.34375 -19.726562 9.046875 -19.695312 8.734375 -19.78125 C 7.171875 -20.226562 5.601562 -19.789062 4.03125 -18.46875 C 2.144531 -16.875 1.34375 -14.816406 1.625 -12.296875 C 1.78125 -10.796875 2.566406 -9.125 3.984375 -7.28125 C 5.398438 -5.445312 7.035156 -3.539062 8.890625 -1.5625 C 9.835938 -0.5 10.648438 0.59375 11.328125 1.71875 C 11.585938 1.257812 11.859375 0.757812 12.140625 0.21875 C 12.398438 -0.226562 12.929688 -1.113281 13.734375 -2.4375 C 14.535156 -3.769531 15.472656 -5.328125 16.546875 -7.109375 C 17.628906 -8.898438 18.753906 -10.738281 19.921875 -12.625 C 21.085938 -14.507812 22.144531 -16.238281 23.09375 -17.8125 C 23.25 -18.070312 23.484375 -18.25 23.796875 -18.34375 C 24.117188 -18.4375 24.421875 -18.382812 24.703125 -18.1875 Z M 10.125 16.609375 C 10.332031 16.128906 10.570312 15.441406 10.84375 14.546875 C 11.125 13.648438 11.316406 12.582031 11.421875 11.34375 C 11.535156 10.113281 11.414062 8.785156 11.0625 7.359375 C 9.613281 10.273438 8.578125 12.671875 7.953125 14.546875 C 7.335938 16.429688 6.96875 17.847656 6.84375 18.796875 C 6.726562 19.753906 6.734375 20.363281 6.859375 20.625 C 7.140625 20.582031 7.597656 20.238281 8.234375 19.59375 C 8.878906 18.957031 9.507812 17.960938 10.125 16.609375 Z M 10.125 16.609375 "
                        />
                      </g>
                    </g>
                  </g>
                </g>
              </g>

              {/* ── + SIGN ── */}
              <g transform="matrix(1, 0, 0, 1, 98, 170)">
                <g clipPath="url(#d3hw-c2)">
                  <g className="d3-letter" style={{ "--d": "1.82s" }}>
                    <g transform="translate(10.348849, 50.321237)">
                      <g>
                        <path
                          pathLength="1"
                          d="M 10.71875 -15.40625 C 10.976562 -15.457031 11.222656 -15.410156 11.453125 -15.265625 C 11.679688 -15.128906 11.820312 -14.9375 11.875 -14.6875 C 11.925781 -14.425781 11.875 -14.1875 11.71875 -13.96875 C 11.5625 -13.75 11.351562 -13.613281 11.09375 -13.5625 C 10.570312 -13.457031 9.953125 -13.316406 9.234375 -13.140625 C 8.515625 -12.960938 7.757812 -12.78125 6.96875 -12.59375 C 6.882812 -12.207031 6.800781 -11.828125 6.71875 -11.453125 C 6.632812 -11.085938 6.554688 -10.75 6.484375 -10.4375 C 6.410156 -10 6.335938 -9.582031 6.265625 -9.1875 C 6.191406 -8.800781 6.117188 -8.46875 6.046875 -8.1875 C 5.898438 -7.5625 5.789062 -7.148438 5.71875 -6.953125 C 5.644531 -6.753906 5.441406 -6.617188 5.109375 -6.546875 C 4.859375 -6.492188 4.625 -6.535156 4.40625 -6.671875 C 4.1875 -6.816406 4.050781 -7.015625 4 -7.265625 C 3.96875 -7.429688 3.972656 -7.59375 4.015625 -7.75 C 4.035156 -7.832031 4.109375 -8.179688 4.234375 -8.796875 C 4.359375 -9.421875 4.503906 -10.097656 4.671875 -10.828125 C 4.710938 -11.046875 4.753906 -11.253906 4.796875 -11.453125 C 4.847656 -11.660156 4.882812 -11.863281 4.90625 -12.0625 C 4.40625 -11.9375 3.863281 -11.800781 3.28125 -11.65625 C 2.894531 -11.582031 2.535156 -11.503906 2.203125 -11.421875 C 1.867188 -11.335938 1.566406 -11.25 1.296875 -11.15625 C 1.296875 -11.15625 1.273438 -11.15625 1.234375 -11.15625 C 1.003906 -11.101562 0.773438 -11.144531 0.546875 -11.28125 C 0.328125 -11.414062 0.195312 -11.597656 0.15625 -11.828125 C 0.09375 -12.085938 0.117188 -12.328125 0.234375 -12.546875 C 0.359375 -12.773438 0.546875 -12.929688 0.796875 -13.015625 C 1.085938 -13.078125 1.398438 -13.148438 1.734375 -13.234375 C 2.078125 -13.316406 2.4375 -13.410156 2.8125 -13.515625 C 3.226562 -13.597656 3.640625 -13.695312 4.046875 -13.8125 C 4.460938 -13.925781 4.898438 -14.03125 5.359375 -14.125 C 5.648438 -15.625 5.863281 -16.660156 6 -17.234375 C 6.144531 -17.816406 6.222656 -18.160156 6.234375 -18.265625 C 6.316406 -18.515625 6.476562 -18.695312 6.71875 -18.8125 C 6.96875 -18.925781 7.21875 -18.945312 7.46875 -18.875 C 7.6875 -18.789062 7.851562 -18.628906 7.96875 -18.390625 C 8.082031 -18.160156 8.113281 -17.921875 8.0625 -17.671875 C 8.019531 -17.578125 7.9375 -17.238281 7.8125 -16.65625 C 7.6875 -16.082031 7.550781 -15.414062 7.40625 -14.65625 C 8.039062 -14.8125 8.644531 -14.953125 9.21875 -15.078125 C 9.800781 -15.210938 10.300781 -15.320312 10.71875 -15.40625 Z M 10.71875 -15.40625 "
                        />
                      </g>
                    </g>
                  </g>
                </g>
              </g>

              {/* ── SWASH RIGHT ── */}
              <g clipPath="url(#d3hw-c3)">
                <g clipPath="url(#d3hw-c4)">
                  <g clipPath="url(#d3hw-c5)">
                    <g className="d3-swash" style={{ "--d": "1.95s" }}>
                      <path
                        pathLength="1"
                        fillRule="evenodd"
                        d="M 256.238281 185.9375 C 257.128906 186.25 257.105469 186.574219 257.613281 186.601562 C 260.550781 186.707031 264.742188 188.179688 266.351562 188.683594 C 292.609375 196.730469 311.632812 165.730469 350.972656 166.273438 C 351.308594 166.273438 352.1875 165.808594 351.414062 165.164062 C 345.746094 164.417969 339.691406 163.941406 328.308594 166.875 C 315.757812 170.089844 293.324219 184.773438 282.523438 187.453125 C 268.515625 190.921875 261.398438 183.898438 256.261719 185.949219 Z M 256.238281 185.9375 "
                      />
                    </g>
                  </g>
                </g>
              </g>

              {/* ── SWASH LEFT ── */}
              <g clipPath="url(#d3hw-c6)">
                <g clipPath="url(#d3hw-c7)">
                  <g transform="matrix(1, 0, 0, 1, 18, 117)">
                    <g clipPath="url(#d3hw-c11)">
                      <g clipPath="url(#d3hw-c8)">
                        <g clipPath="url(#d3hw-c9)">
                          <g clipPath="url(#d3hw-c10)">
                            <g className="d3-swash" style={{ "--d": "2.05s" }}>
                              <path
                                pathLength="1"
                                fillRule="evenodd"
                                d="M 4.054688 44.121094 C 4.5625 43.328125 4.347656 43.085938 4.734375 42.753906 C 6.992188 40.875 9.40625 37.144531 10.371094 35.757812 C 26.214844 13.328125 60.226562 26.203125 91.007812 1.695312 C 91.273438 1.488281 92.253906 1.320312 92.035156 2.300781 C 88.011719 6.363281 83.511719 10.445312 72.71875 15.089844 C 60.824219 20.226562 34.09375 22.347656 23.914062 26.835938 C 10.710938 32.664062 9.378906 42.574219 4.0625 44.097656 Z M 4.054688 44.121094 "
                              />
                            </g>
                          </g>
                        </g>
                      </g>
                    </g>
                  </g>
                </g>
              </g>

              {/* ── LIYA ── */}
              <g transform="matrix(1, 0, 0, 1, 141, 135)">
                <g clipPath="url(#d3hw-c12)">
                  {/* L */}
                  <g className="d3-letter" style={{ "--d": "2.3s" }}>
                    <g transform="translate(10.347808, 81.289171)">
                      <g>
                        <path
                          pathLength="1"
                          d="M 49.3125 1.75 C 49.632812 1.695312 49.925781 1.765625 50.1875 1.953125 C 50.445312 2.140625 50.601562 2.390625 50.65625 2.703125 C 50.707031 3.035156 50.628906 3.332031 50.421875 3.59375 C 50.210938 3.851562 49.945312 4.007812 49.625 4.0625 C 47.550781 4.351562 45.289062 5.191406 42.84375 6.578125 C 40.40625 7.960938 37.894531 9.625 35.3125 11.5625 C 32.726562 13.507812 30.226562 15.445312 27.8125 17.375 C 24.570312 19.96875 21.640625 22.21875 19.015625 24.125 C 16.390625 26.03125 14.085938 27.144531 12.109375 27.46875 C 11.015625 27.644531 10.066406 27.53125 9.265625 27.125 C 7.171875 26.019531 6.289062 23 6.625 18.0625 C 6.84375 15.039062 7.117188 11.546875 7.453125 7.578125 C 7.785156 3.617188 8.171875 -0.597656 8.609375 -5.078125 C 9.054688 -9.554688 9.515625 -14.097656 9.984375 -18.703125 C 10.460938 -23.316406 10.960938 -27.753906 11.484375 -32.015625 C 12.179688 -37.710938 12.804688 -42.519531 13.359375 -46.4375 C 13.910156 -50.351562 14.40625 -53.566406 14.84375 -56.078125 C 15.289062 -58.585938 15.691406 -60.535156 16.046875 -61.921875 C 16.398438 -63.304688 16.734375 -64.300781 17.046875 -64.90625 C 17.367188 -65.507812 17.65625 -65.875 17.90625 -66 C 18.15625 -66.125 18.40625 -66.1875 18.65625 -66.1875 C 19.445312 -66.1875 19.984375 -65.679688 20.265625 -64.671875 C 20.546875 -63.660156 20.78125 -62.054688 20.96875 -59.859375 C 20.976562 -59.523438 20.878906 -59.238281 20.671875 -59 C 20.472656 -58.757812 20.207031 -58.632812 19.875 -58.625 C 19.550781 -58.570312 19.257812 -58.644531 19 -58.84375 C 18.738281 -59.050781 18.597656 -59.328125 18.578125 -59.671875 C 18.535156 -60.398438 18.488281 -61.070312 18.4375 -61.6875 C 18.132812 -60.507812 17.753906 -58.703125 17.296875 -56.265625 C 16.835938 -53.835938 16.304688 -50.5 15.703125 -46.25 C 15.109375 -42 14.414062 -36.539062 13.625 -29.875 C 13.132812 -25.738281 12.65625 -21.445312 12.1875 -17 C 11.71875 -12.5625 11.269531 -8.1875 10.84375 -3.875 C 10.425781 0.4375 10.054688 4.488281 9.734375 8.28125 C 9.410156 12.082031 9.164062 15.394531 9 18.21875 C 8.90625 19.894531 8.921875 21.207031 9.046875 22.15625 C 9.179688 23.101562 9.375 23.785156 9.625 24.203125 C 9.875 24.628906 10.117188 24.898438 10.359375 25.015625 C 10.984375 25.335938 11.867188 25.269531 13.015625 24.8125 C 14.160156 24.363281 15.460938 23.648438 16.921875 22.671875 C 18.378906 21.703125 19.914062 20.585938 21.53125 19.328125 C 23.15625 18.066406 24.757812 16.8125 26.34375 15.5625 C 28.820312 13.550781 31.398438 11.539062 34.078125 9.53125 C 36.753906 7.53125 39.382812 5.804688 41.96875 4.359375 C 44.5625 2.910156 47.007812 2.039062 49.3125 1.75 Z M 49.3125 1.75 "
                        />
                      </g>
                    </g>
                  </g>
                  {/* i */}
                  <g className="d3-letter" style={{ "--d": "2.52s" }}>
                    <g transform="translate(51.369373, 74.692402)">
                      <g>
                        <path
                          pathLength="1"
                          d="M 15.5 -16.640625 C 15.8125 -16.535156 16.019531 -16.328125 16.125 -16.015625 C 16.238281 -15.703125 16.242188 -15.382812 16.140625 -15.0625 C 15.554688 -13.726562 14.800781 -12.21875 13.875 -10.53125 C 12.957031 -8.851562 11.914062 -7.234375 10.75 -5.671875 C 9.59375 -4.109375 8.410156 -2.769531 7.203125 -1.65625 C 6.003906 -0.550781 4.835938 0.0859375 3.703125 0.265625 C 3.222656 0.347656 2.785156 0.335938 2.390625 0.234375 C 0.929688 -0.160156 -0.0546875 -1.503906 -0.578125 -3.796875 C -1.097656 -6.085938 -1.148438 -9.4375 -0.734375 -13.84375 C -0.703125 -14.175781 -0.554688 -14.445312 -0.296875 -14.65625 C -0.046875 -14.863281 0.222656 -14.945312 0.515625 -14.90625 C 0.847656 -14.882812 1.117188 -14.742188 1.328125 -14.484375 C 1.535156 -14.234375 1.625 -13.957031 1.59375 -13.65625 C 1.351562 -10.96875 1.273438 -8.851562 1.359375 -7.3125 C 1.441406 -5.78125 1.597656 -4.640625 1.828125 -3.890625 C 2.054688 -3.140625 2.289062 -2.648438 2.53125 -2.421875 C 2.769531 -2.191406 2.910156 -2.082031 2.953125 -2.09375 C 3.523438 -1.925781 4.265625 -2.21875 5.171875 -2.96875 C 6.085938 -3.71875 7.078125 -4.769531 8.140625 -6.125 C 9.203125 -7.476562 10.242188 -9.007812 11.265625 -10.71875 C 12.296875 -12.4375 13.1875 -14.207031 13.9375 -16.03125 C 14.09375 -16.34375 14.316406 -16.550781 14.609375 -16.65625 C 14.910156 -16.757812 15.207031 -16.753906 15.5 -16.640625 Z M 0.453125 -19.203125 C -0.359375 -19.109375 -0.804688 -19.269531 -0.890625 -19.6875 C -0.984375 -20.101562 -0.875 -20.5625 -0.5625 -21.0625 C -0.25 -21.570312 0.09375 -21.9375 0.46875 -22.15625 C 1.082031 -22.46875 1.5625 -22.507812 1.90625 -22.28125 C 2.257812 -22.050781 2.460938 -21.710938 2.515625 -21.265625 C 2.566406 -20.816406 2.425781 -20.378906 2.09375 -19.953125 C 1.769531 -19.523438 1.222656 -19.273438 0.453125 -19.203125 Z M 0.453125 -19.203125 "
                        />
                      </g>
                    </g>
                  </g>
                  {/* y */}
                  <g className="d3-letter" style={{ "--d": "2.74s" }}>
                    <g transform="translate(66.676828, 72.230776)">
                      <g>
                        <path
                          pathLength="1"
                          d="M 27.453125 -18.078125 C 27.679688 -17.859375 27.785156 -17.582031 27.765625 -17.25 C 27.742188 -16.914062 27.617188 -16.644531 27.390625 -16.4375 C 24.765625 -14.039062 22.144531 -11.476562 19.53125 -8.75 C 16.925781 -6.03125 14.441406 -3.039062 12.078125 0.21875 C 11.984375 14.851562 11.242188 26.835938 9.859375 36.171875 C 9.546875 38.066406 9.1875 40.007812 8.78125 42 C 8.375 44 7.882812 45.878906 7.3125 47.640625 C 6.75 49.398438 6.09375 50.910156 5.34375 52.171875 C 4.601562 53.429688 3.769531 54.285156 2.84375 54.734375 C 2.570312 54.847656 2.28125 54.929688 1.96875 54.984375 C 1.03125 55.140625 0.15625 54.867188 -0.65625 54.171875 C -1.675781 53.210938 -2.5 51.644531 -3.125 49.46875 C -3.75 47.289062 -4.117188 44.679688 -4.234375 41.640625 C -4.347656 38.597656 -4.160156 35.316406 -3.671875 31.796875 C -3.179688 28.285156 -2.328125 24.738281 -1.109375 21.15625 C 0.335938 16.820312 1.984375 12.875 3.828125 9.3125 C 5.671875 5.75 7.65625 2.453125 9.78125 -0.578125 C 9.757812 -4.460938 9.722656 -8.535156 9.671875 -12.796875 C 9.273438 -11.898438 8.84375 -10.988281 8.375 -10.0625 C 7.90625 -9.144531 7.421875 -8.273438 6.921875 -7.453125 C 6.265625 -6.347656 5.617188 -5.597656 4.984375 -5.203125 C 4.347656 -4.816406 3.78125 -4.625 3.28125 -4.625 C 2.789062 -4.632812 2.410156 -4.691406 2.140625 -4.796875 C 1.171875 -5.191406 0.414062 -6.035156 -0.125 -7.328125 C -0.664062 -8.628906 -0.960938 -10.226562 -1.015625 -12.125 C -1.078125 -14.019531 -0.863281 -16.039062 -0.375 -18.1875 C -0.300781 -18.488281 -0.125 -18.722656 0.15625 -18.890625 C 0.4375 -19.066406 0.734375 -19.113281 1.046875 -19.03125 C 1.328125 -18.957031 1.5625 -18.789062 1.75 -18.53125 C 1.945312 -18.28125 2.007812 -17.976562 1.9375 -17.625 C 1.445312 -15.601562 1.226562 -13.820312 1.28125 -12.28125 C 1.34375 -10.738281 1.550781 -9.507812 1.90625 -8.59375 C 2.257812 -7.675781 2.640625 -7.148438 3.046875 -7.015625 C 3.523438 -6.796875 4.140625 -7.328125 4.890625 -8.609375 C 5.546875 -9.703125 6.175781 -10.898438 6.78125 -12.203125 C 7.394531 -13.503906 7.945312 -14.742188 8.4375 -15.921875 C 8.9375 -17.097656 9.335938 -18.054688 9.640625 -18.796875 C 9.859375 -19.410156 10.289062 -19.671875 10.9375 -19.578125 C 11.582031 -19.429688 11.910156 -19.046875 11.921875 -18.421875 C 12.054688 -13.273438 12.125 -8.390625 12.125 -3.765625 C 14.28125 -6.546875 16.507812 -9.113281 18.8125 -11.46875 C 21.125 -13.832031 23.457031 -16.066406 25.8125 -18.171875 C 26.03125 -18.378906 26.304688 -18.476562 26.640625 -18.46875 C 26.972656 -18.457031 27.242188 -18.328125 27.453125 -18.078125 Z M 7.515625 35.84375 C 8.785156 27.363281 9.53125 16.671875 9.75 3.765625 C 8.070312 6.359375 6.503906 9.144531 5.046875 12.125 C 3.585938 15.113281 2.273438 18.375 1.109375 21.90625 C -0.140625 25.707031 -0.984375 29.320312 -1.421875 32.75 C -1.867188 36.175781 -2.007812 39.257812 -1.84375 42 C -1.675781 44.738281 -1.332031 47.007812 -0.8125 48.8125 C -0.289062 50.625 0.28125 51.804688 0.90625 52.359375 C 1.15625 52.578125 1.347656 52.679688 1.484375 52.671875 C 1.628906 52.671875 1.738281 52.648438 1.8125 52.609375 C 1.96875 52.535156 2.238281 52.285156 2.625 51.859375 C 3.007812 51.441406 3.484375 50.660156 4.046875 49.515625 C 4.617188 48.378906 5.203125 46.71875 5.796875 44.53125 C 6.398438 42.34375 6.972656 39.445312 7.515625 35.84375 Z M 7.515625 35.84375 "
                        />
                      </g>
                    </g>
                  </g>
                  {/* a */}
                  <g className="d3-letter" style={{ "--d": "2.96s" }}>
                    <g transform="translate(93.480006, 67.920498)">
                      <g>
                        <path
                          pathLength="1"
                          d="M 23.1875 -17.515625 C 23.5 -17.429688 23.726562 -17.242188 23.875 -16.953125 C 24.019531 -16.671875 24.054688 -16.382812 23.984375 -16.09375 C 23.472656 -14.394531 22.890625 -12.6875 22.234375 -10.96875 C 21.578125 -9.25 20.859375 -7.648438 20.078125 -6.171875 C 19.296875 -4.703125 18.441406 -3.488281 17.515625 -2.53125 C 16.597656 -1.582031 15.617188 -1.023438 14.578125 -0.859375 C 14.367188 -0.816406 14.140625 -0.800781 13.890625 -0.8125 C 11.109375 -0.894531 8.851562 -4.253906 7.125 -10.890625 C 6.582031 -9.847656 5.953125 -8.675781 5.234375 -7.375 C 4.515625 -6.082031 3.742188 -4.796875 2.921875 -3.515625 C 2.773438 -3.316406 2.546875 -3.019531 2.234375 -2.625 C 1.929688 -2.226562 1.554688 -1.890625 1.109375 -1.609375 C 0.660156 -1.328125 0.171875 -1.269531 -0.359375 -1.4375 C -1.378906 -1.632812 -2.007812 -2.601562 -2.25 -4.34375 C -2.414062 -5.351562 -2.425781 -6.632812 -2.28125 -8.1875 C -2.144531 -9.738281 -1.84375 -11.328125 -1.375 -12.953125 C -0.90625 -14.585938 -0.296875 -16.066406 0.453125 -17.390625 C 1.210938 -18.710938 2.148438 -19.664062 3.265625 -20.25 C 3.941406 -20.664062 4.816406 -20.863281 5.890625 -20.84375 C 6.960938 -20.820312 8.175781 -20.238281 9.53125 -19.09375 C 9.78125 -18.882812 9.914062 -18.617188 9.9375 -18.296875 C 9.96875 -17.972656 9.878906 -17.6875 9.671875 -17.4375 C 9.460938 -17.195312 9.191406 -17.054688 8.859375 -17.015625 C 8.535156 -16.984375 8.25 -17.078125 8 -17.296875 C 6.863281 -18.273438 5.945312 -18.675781 5.25 -18.5 C 4.550781 -18.332031 3.988281 -18.023438 3.5625 -17.578125 C 2.78125 -16.785156 2.125 -15.726562 1.59375 -14.40625 C 1.0625 -13.082031 0.660156 -11.710938 0.390625 -10.296875 C 0.117188 -8.890625 -0.0234375 -7.59375 -0.046875 -6.40625 C -0.0664062 -5.226562 0.00390625 -4.382812 0.171875 -3.875 C 0.285156 -3.976562 0.398438 -4.101562 0.515625 -4.25 C 0.640625 -4.394531 0.769531 -4.578125 0.90625 -4.796875 C 1.90625 -6.335938 2.800781 -7.84375 3.59375 -9.3125 C 4.394531 -10.78125 5.078125 -12.078125 5.640625 -13.203125 C 5.796875 -13.515625 5.941406 -13.816406 6.078125 -14.109375 C 6.210938 -14.398438 6.347656 -14.660156 6.484375 -14.890625 C 6.734375 -15.335938 7.132812 -15.523438 7.6875 -15.453125 C 8.226562 -15.410156 8.566406 -15.109375 8.703125 -14.546875 C 9.242188 -11.960938 9.796875 -9.894531 10.359375 -8.34375 C 10.921875 -6.800781 11.441406 -5.664062 11.921875 -4.9375 C 12.410156 -4.207031 12.84375 -3.722656 13.21875 -3.484375 C 13.59375 -3.253906 13.859375 -3.15625 14.015625 -3.1875 C 14.796875 -3.132812 15.894531 -4.113281 17.3125 -6.125 C 18.738281 -8.132812 20.195312 -11.660156 21.6875 -16.703125 C 21.800781 -17.054688 22 -17.304688 22.28125 -17.453125 C 22.570312 -17.609375 22.875 -17.628906 23.1875 -17.515625 Z M 23.1875 -17.515625 "
                        />
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </svg>
          </h1>
          <div className="d3__hero-date-wrap">
            <div className="d3__hero-date-rule" />
            <span className="d3__hero-date">May 2 · 2026</span>
            <div className="d3__hero-date-rule" />
          </div>
          <AddToCalendarButton triggerClassName="d3__hero-rsvp" label="RSVP" />
        </div>
      </section>
      {/* ── FAMILY ── */}
      <section ref={familyRef} className="d3__family">
        <div className="d3__section-header d3-animate">
          <span className="d3__section-tag">With love, from us</span>
          <h2>
            Our <em>Families</em>
          </h2>
          <div className="d3__ornament">
            <div className="d3__ornament-line" />
            <div className="d3__ornament-diamond" />
            <div className="d3__ornament-line" />
          </div>
        </div>
        <div className="d3__family-pair">
          <div
            className="d3__family-card d3-animate"
            style={{ "--delay": "0.1s" }}
          >
            <div className="d3__family-photo">
              <img
                src="/wedding/images/family/pichappillil.webp"
                alt="The Pichappillil Family"
              />
            </div>
            <p className="d3__family-card-label">Groom's Family</p>
            <h3 className="d3__family-card-name">The Pichappillil Family</h3>
            <p className="d3__family-card-parents">
              Mr. &amp; Mrs. Mathew George
            </p>
            <ul className="d3__family-card-members">
              <li>
                Mathew George <span>Father</span>
              </li>
              <li>
                Lovely Mathew <span>Mother</span>
              </li>
              <li>
                Divya Mathew <span>Sister</span>
              </li>
            </ul>
          </div>

          <div className="d3__family-center" aria-hidden="true">
            <div className="d3__family-center-line" />
            <div className="d3__family-ampersand">&amp;</div>
            <div className="d3__family-center-line" />
          </div>

          <div
            className="d3__family-card d3-animate"
            style={{ "--delay": "0.2s" }}
          >
            <div className="d3__family-photo">
              <img
                src="/wedding/images/family/pattamana.webp"
                alt="The Pattamana Family"
              />
            </div>
            <p className="d3__family-card-label">Bride's Family</p>
            <h3 className="d3__family-card-name">The Pattamana Family</h3>
            <p className="d3__family-card-parents">
              Mr. &amp; Mrs. Biju Philip
            </p>
            <ul className="d3__family-card-members">
              <li>
                Biju Philip <span>Father</span>
              </li>
              <li>
                Jessy Joseph <span>Mother</span>
              </li>
            </ul>
          </div>
        </div>
      </section>
      {/* ── SCHEDULE ── */}
      <section ref={scheduleRef} className="d3__schedule">
        <div className="d3__section-header d3-animate">
          <span className="d3__section-tag">May 2, 2026</span>
          <h2>
            A Day to <em>Remember</em>
          </h2>
          <div className="d3__ornament">
            <div className="d3__ornament-line" />
            <div className="d3__ornament-diamond" />
            <div className="d3__ornament-line" />
          </div>
        </div>
        <div className="d3__timeline">
          {SCHEDULE.map((item, i) => {
            const isLeft = i % 2 === 0;
            return (
              <div
                key={i}
                className="d3__tl-item d3-animate"
                style={{ "--delay": `${i * 0.08}s` }}
              >
                {isLeft ? (
                  <>
                    <div className="d3__tl-content d3__tl-content--left">
                      <span className="d3__tl-index" aria-hidden="true">
                        {ROMAN[i]}
                      </span>
                      <p className="d3__tl-time">{item.time}</p>
                      <h4 className="d3__tl-event">{item.event}</h4>
                      {item.desc && <p className="d3__tl-desc">{item.desc}</p>}
                    </div>
                    <div className="d3__tl-node" />
                    <div className="d3__tl-empty" aria-hidden="true" />
                  </>
                ) : (
                  <>
                    <div className="d3__tl-empty" aria-hidden="true" />
                    <div className="d3__tl-node" />
                    <div className="d3__tl-content d3__tl-content--right">
                      <span className="d3__tl-index" aria-hidden="true">
                        {ROMAN[i]}
                      </span>
                      <p className="d3__tl-time">{item.time}</p>
                      <h4 className="d3__tl-event">{item.event}</h4>
                      {item.desc && <p className="d3__tl-desc">{item.desc}</p>}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </section>
      {/* ── VENUE ── */}
      <section ref={venueRef} className="d3__venue">
        <div className="d3__section-header d3-animate">
          <span className="d3__section-tag">Find Us Here</span>
          <h2>
            The <em>Venues</em>
          </h2>
          <div className="d3__ornament">
            <div className="d3__ornament-line" />
            <div className="d3__ornament-diamond" />
            <div className="d3__ornament-line" />
          </div>
        </div>
        <div
          className="d3__venue-inner d3-animate"
          style={{ "--delay": "0.1s" }}
        >
          <div className="d3__venue-frame">
            <APIProvider apiKey={MAPS_API_KEY}>
              <div className="d3__venue-map">
                <Map
                  mapId={MAP_ID}
                  defaultCenter={CHURCH_LOCATION}
                  defaultZoom={17}
                  gestureHandling="cooperative"
                  disableDefaultUI
                  backgroundColor={"#faf6f4"}
                >
                  <AdvancedMarker position={CHURCH_LOCATION}>
                    <VenuePin />
                  </AdvancedMarker>
                </Map>
                <div className="d3__map-overlay" aria-hidden="true" />
              </div>
            </APIProvider>
          </div>
          <div className="d3__venue-info">
            <p className="d3__venue-label">Where the celebration begins</p>
            <h3 className="d3__venue-name">Little Flower Church</h3>
            <p className="d3__venue-address">
              Puthencruz,
              <br />
              Kochi, Kerala
            </p>
            <div className="d3__venue-ornament">
              <div className="d3__venue-ornament-line" />
              <span className="d3__venue-ornament-text">Venue</span>
              <div className="d3__venue-ornament-line" />
            </div>
            <p className="d3__venue-desc">
              "Amidst the tranquil beauty of Puthencruz, Kochi, surrounded by
              prayers, blessings, and the warmth of loved ones, we come together
              to celebrate the beginning of our forever."
            </p>
            <a
              href="https://maps.app.goo.gl/Sw3ab4cf3RoG54bM8"
              target="_blank"
              rel="noopener noreferrer"
              className="d3__venue-cta"
            >
              View on Maps →
            </a>
          </div>
        </div>

        {/* ── RECEPTION VENUE ── */}
        <div
          className="d3__venue-inner d3__venue-inner--second d3-animate"
          style={{ "--delay": "0.2s" }}
        >
          <div className="d3__venue-info">
            <p className="d3__venue-label">Followed by reception at</p>
            <h3 className="d3__venue-name">Eden Convention Center</h3>
            <p className="d3__venue-address">
              Muvattupuzha,
              <br />
              Kochi, Kerala
            </p>
            <div className="d3__venue-ornament">
              <div className="d3__venue-ornament-line" />
              <span className="d3__venue-ornament-text">Venue</span>
              <div className="d3__venue-ornament-line" />
            </div>
            <p className="d3__venue-desc">
              "At Muvattupuzha, we will be hosting our wedding reception,
              bringing together family and friends to share in an evening of
              love, celebration, and cherished moments."
            </p>
            <a
              href="https://maps.app.goo.gl/k78iF3z8rpcwKiCf9"
              target="_blank"
              rel="noopener noreferrer"
              className="d3__venue-cta"
            >
              View on Maps →
            </a>
          </div>
          <div className="d3__venue-frame">
            <APIProvider apiKey={MAPS_API_KEY}>
              <div className="d3__venue-map">
                <Map
                  mapId={MAP_ID}
                  defaultCenter={HALL_LOCATION}
                  defaultZoom={17}
                  gestureHandling="cooperative"
                  disableDefaultUI
                  backgroundColor={"#faf6f4"}
                >
                  <AdvancedMarker position={HALL_LOCATION}>
                    <VenuePin />
                  </AdvancedMarker>
                </Map>
                <div className="d3__map-overlay" aria-hidden="true" />
              </div>
            </APIProvider>
          </div>
        </div>
      </section>
      {/* ── GALLERY ── */}
      <section ref={galleryRef} className="d3__gallery">
        <div className="d3__section-header d3-animate">
          <span className="d3__section-tag">A Glimpse of Us</span>
          <h2>
            Our <em>Story</em>
          </h2>
          <div className="d3__ornament">
            <div className="d3__ornament-line" />
            <div className="d3__ornament-diamond" />
            <div className="d3__ornament-line" />
          </div>
        </div>
        <div className="d3__gallery-grid">
          <div
            className="d3__gallery-item d3__gallery-item--tall d3-animate"
            style={{ "--delay": "0s" }}
            onClick={() =>
              openImageLightbox("/wedding/images/gallery/ili.webp")
            }
          >
            <img src="/wedding/images/gallery/ili.webp" alt="" />
          </div>
          <div
            className="d3__gallery-item d3-animate"
            style={{ "--delay": "0.06s" }}
            onClick={() =>
              openImageLightbox("/wedding/images/gallery/karangi.webp")
            }
          >
            <img src="/wedding/images/gallery/karangi.webp" alt="" />
          </div>
          <div
            className="d3__gallery-item d3-animate"
            style={{ "--delay": "0.12s" }}
            onClick={() =>
              openImageLightbox("/wedding/images/gallery/ntho-cheyynu.webp")
            }
          >
            <img src="/wedding/images/gallery/ntho-cheyynu.webp" alt="" />
          </div>
          <div
            className="d3__gallery-item d3__gallery-item--wide d3-animate"
            style={{ "--delay": "0.18s" }}
            onClick={() =>
              openImageLightbox("/wedding/images/gallery/otf-baby.webp")
            }
          >
            <img src="/wedding/images/gallery/otf-baby.webp" alt="" />
          </div>
          <div
            className="d3__gallery-item d3-animate"
            style={{ "--delay": "0.24s" }}
            onClick={() =>
              openImageLightbox("/wedding/images/gallery/pokki.webp")
            }
          >
            <img src="/wedding/images/gallery/pokki.webp" alt="" />
          </div>
          <div
            className="d3__gallery-item d3__gallery-item--tall d3-animate"
            style={{ "--delay": "0.30s" }}
            onClick={() =>
              openImageLightbox("/wedding/images/gallery/povva.webp")
            }
          >
            <img src="/wedding/images/gallery/povva.webp" alt="" />
          </div>
          <div
            className="d3__gallery-item d3__gallery-item--video d3__gallery-item--wide d3-animate"
            style={{ "--delay": "0.36s" }}
            onClick={() =>
              openVideoLightbox(
                STORY_VIDEO.id,
                STORY_VIDEO.title,
                STORY_VIDEO.aspectRatio,
                STORY_VIDEO.aspectRatioValue,
              )
            }
          >
            <img
              src={`https://i.ytimg.com/vi/${STORY_VIDEO.id}/hqdefault.jpg`}
              alt={STORY_VIDEO.title}
            />
            <div className="d3__gallery-video-badge" aria-hidden="true">
              <span className="d3__gallery-video-play">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M9 7.5 17 12l-8 4.5V7.5Z" fill="currentColor" />
                </svg>
              </span>
              <span>Play Video</span>
            </div>
          </div>

          <div
            className="d3__gallery-item d3__gallery-item--wide d3-animate"
            style={{ "--delay": "0.30s" }}
            onClick={() =>
              openImageLightbox("/wedding/images/gallery/pidi_kai.webp")
            }
          >
            <img src="/wedding/images/gallery/pidi_kai.webp" alt="" />
          </div>
        </div>
      </section>
      .{/* ── LIGHTBOX ── */}
      {lightboxItem && (
        <div
          className={`d3__lightbox${lightboxClosing ? " closing" : ""}`}
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          onAnimationEnd={(e) => {
            if (e.target === e.currentTarget && lightboxClosing) {
              setLightboxItem(null);
              setLightboxClosing(false);
            }
          }}
        >
          <button
            className="d3__lightbox-close"
            onClick={closeLightbox}
            aria-label="Close preview"
          >
            &#x2715;
          </button>
          {lightboxItem.type === "image" ? (
            <img
              className="d3__lightbox-img"
              src={lightboxItem.src}
              alt=""
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <div
              className="d3__lightbox-video-wrap"
              style={{
                "--video-ratio": lightboxItem.aspectRatio || "16 / 9",
                "--video-ratio-dec": lightboxItem.aspectRatioValue || 1.7778,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <YouTube
                className="d3__lightbox-video"
                videoId={lightboxItem.videoId}
                title={lightboxItem.title}
                opts={{
                  width: "100%",
                  height: "100%",
                  playerVars: {
                    autoplay: 1,
                    rel: 0,
                    modestbranding: 1,
                  },
                }}
              />
            </div>
          )}
        </div>
      )}
      {/* ── COUNTDOWN ── */}
      <section
        ref={countdownRef}
        className={`d3__countdown${countdownComplete ? " d3__countdown--complete" : ""}`}
      >
        <div className="d3__countdown-bg" aria-hidden="true">
          <img src="/wedding/images/maanam-nokki.webp" alt="" />
        </div>
        <div className="d3__countdown-vignette" aria-hidden="true" />
        {countdownComplete && (
          <div className="d3__confetti" aria-hidden="true">
            {CONFETTI_PIECES.map((piece) => (
              <span
                key={piece.id}
                className={`d3__confetti-piece ${piece.shape}`}
                style={{
                  "--x": piece.x,
                  "--drift": piece.drift,
                  "--delay": piece.delay,
                  "--duration": piece.duration,
                  "--w": piece.width,
                  "--h": piece.height,
                  "--spin": piece.spin,
                  "--confetti-color": piece.color,
                }}
              />
            ))}
          </div>
        )}
        <p className="d3__countdown-eyebrow d3__countdown-copy d3-animate">
          <span className="d3__countdown-copy-before">
            The Clock is Ticking
          </span>
          <span className="d3__countdown-copy-after">Just Married</span>
        </p>
        <p
          className="d3__countdown-title d3__countdown-copy d3-animate"
          style={{ "--delay": "0.05s" }}
        >
          <span className="d3__countdown-copy-before">
            Until We Begin Our Forever
          </span>
          <span className="d3__countdown-copy-after">
            Our Forever Has Begun
          </span>
        </p>
        <div className="d3__countdown-stage">
          <div
            className="d3__countdown-row d3-animate"
            style={{ "--delay": "0.1s" }}
            aria-hidden={countdownComplete}
          >
            {[
              { value: countdown.days, label: "Days" },
              { value: countdown.hours, label: "Hours" },
              { value: countdown.minutes, label: "Minutes" },
              { value: countdown.seconds, label: "Seconds" },
            ].map(({ value, label }) => (
              <div key={label} className="d3__countdown-unit">
                <span className="d3__countdown-num">{pad(value)}</span>
                <span className="d3__countdown-sublabel">{label}</span>
              </div>
            ))}
          </div>
          <div
            className="d3__countdown-complete"
            role="status"
            aria-hidden={!countdownComplete}
          >
            <span className="d3__countdown-complete-kicker">
              Divins &amp; Liya
            </span>
            <h3>We’re Hitched!</h3>
          </div>
        </div>
        <div
          className="d3__countdown-footer d3-animate"
          style={{ "--delay": "0.15s" }}
        >
          <p className="d3__countdown-tagline">
            May 2, 2026 · Little Flower Church, Puthencruz
          </p>
        </div>
      </section>
      {/* ── FOOTER ── */}
      <footer className="d3__footer">
        <div className="d3__footer-logo">Divins &amp; Liya</div>
        <div className="d3__footer-info">
          May 2, 2026 · Little Flower Church, Puthencruz
        </div>
        <div className="d3__footer-actions">
          <a
            href="/wedding/Divins + Liya - Wedding.pdf"
            download
            className="d3__footer-btn d3__footer-btn--outline"
          >
            <svg
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M8 1v9M4.5 6.5 8 10l3.5-3.5M2 13h12"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Download Invitation
          </a>
          <AddToCalendarButton
            triggerClassName="d3__footer-btn d3__footer-btn--filled"
            label="RSVP"
          />
        </div>
      </footer>
    </div>
  );
};

export default Design3;
